import { FileUp, Plus } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Dropzone } from '@/components/ui/dropzone'
import {
  type ParsedCustomerQuote,
  useConfirmCustomerQuote,
  useParseCustomerQuote,
} from '@/models/customerQuote'
import {
  type CoverageFormData,
  type FeeFormData,
  serializeCoverages,
  serializeFees,
} from '@/routes/Private/App/Customers/Insurance/insuranceFormShared'
import { QuotesEvent, useTrackQuotesEvent } from './quotesAnalytics'
import { UploadQuoteReview } from './UploadQuoteReview'

function extractError(err: unknown, fallback: string): string {
  return (err as { response?: { data?: { error?: string } } }).response?.data?.error || fallback
}

interface UploadQuoteCardProps {
  orgId: string
  /** Carries the new quote's indication id, so the page can bring its card into view. */
  onUploaded: (indicationId: string) => void
}

/**
 * Trailing card in the quotes carousel: an empty slot for a quote the customer got
 * somewhere else, so their own numbers sit alongside ours and compare on the same terms.
 */
export function UploadQuoteCard({ orgId, onUploaded }: UploadQuoteCardProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  // Set once the document has been read — the dialog then shows what we found for the
  // customer to check, rather than putting an unseen extraction on their board.
  const [parsed, setParsed] = useState<ParsedCustomerQuote | null>(null)
  const [coverages, setCoverages] = useState<CoverageFormData[]>([])
  const [fees, setFees] = useState<FeeFormData[]>([])
  const parse = useParseCustomerQuote(orgId)
  const confirm = useConfirmCustomerQuote(orgId)
  const track = useTrackQuotesEvent()

  function openDialog() {
    track(QuotesEvent.UPLOAD_OPENED)
    // Don't wipe a read that's still running (or already landed) — reopening should
    // pick up where it left off rather than start over.
    if (!parse.isPending && !parsed) setFile(null)
    setIsOpen(true)
  }

  async function handleParse() {
    if (!file) return
    try {
      const result = await parse.mutateAsync(file)
      track(QuotesEvent.UPLOAD_PARSED, {
        coverage_count: result.coverages.length,
        fee_count: result.oneTimeFees.length,
      })
      setParsed(result)
      // Reopen if they closed the dialog while it was reading. The request runs to
      // completion either way, and silently binning the result would mean the wait
      // bought them nothing.
      setIsOpen(true)
      setCoverages(
        result.coverages.map((coverage) => ({
          isNew: true,
          coverageType: coverage.coverageType,
          vendorCoverageType: '',
          vendorPolicyId: '',
          premium: coverage.premium,
          limit: coverage.limit,
          deductible: coverage.deductible,
          comprehensiveDeductible: coverage.comprehensiveDeductible ?? null,
          premiumPerUnit: null,
        }))
      )
      setFees(
        result.oneTimeFees.map((fee) => ({
          isNew: true,
          label: fee.label,
          amount: String(fee.amount),
        }))
      )
    } catch (err: unknown) {
      track(QuotesEvent.UPLOAD_FAILED, { step: 'parse' })
      toast.error(extractError(err, "We couldn't read that quote. Please try again."))
    }
  }

  async function handleConfirm() {
    if (!parsed) return
    try {
      // Resolves only once the refetched board holds the quote — the dialog blocks until then.
      const indication = await confirm.mutateAsync({
        documentId: parsed.documentId,
        coverages: serializeCoverages(coverages),
        oneTimeFees: serializeFees(fees),
      })
      track(QuotesEvent.UPLOAD_CONFIRMED, {
        coverage_count: coverages.length,
        fee_count: fees.length,
      })
      toast.success('Quote added')
      setIsOpen(false)
      setParsed(null)
      setFile(null)
      onUploaded(indication.id)
    } catch (err: unknown) {
      track(QuotesEvent.UPLOAD_FAILED, { step: 'confirm' })
      toast.error(extractError(err, "We couldn't add that quote. Please try again."))
    }
  }

  return (
    <>
      <div className="border-ink-100 bg-card/40 flex w-full flex-col items-center justify-center gap-3 rounded-xl border border-dashed p-8 text-center">
        <span className="bg-ink-50 text-ink-400 flex size-11 items-center justify-center rounded-full">
          <FileUp className="size-5" />
        </span>
        <div className="flex flex-col gap-1">
          <span className="text-foreground text-base font-bold">Have another quote?</span>
          <span className="text-ink-400 text-sm">
            Compare all your offers side by side. For larger fleets, Cai brokers can often use competitive
            quotes to negotiate better rates.
          </span>
        </div>
        {/* The read runs on whether or not the dialog is open, so the card carries the
            spinner too — otherwise closing the dialog looks like the upload was dropped. */}
        {parse.isPending ? (
          <Button type="button" variant="outline" size="sm" loading="Reading quote…" />
        ) : (
          <Button type="button" variant="outline" size="sm" className="gap-2" onClick={openDialog}>
            <Plus className="size-4" />
            Upload quote
          </Button>
        )}
      </div>

      {/* Dismissing mid-save would leave them watching a board that hasn't caught up, unexplained. */}
      <Dialog open={isOpen} onOpenChange={(open) => !confirm.isPending && setIsOpen(open)}>
        <DialogContent showCloseButton={!confirm.isPending}>
          <DialogHeader>
            <DialogTitle>{parsed ? 'Check this looks right' : 'Upload quote'}</DialogTitle>
            <DialogDescription>
              {parsed
                ? "Here's what we read off your document — correct anything we got wrong before adding it."
                : "We'll read through the coverages, limits, deductibles and premiums, and create a summary so you can easily compare offers. Reading can take up to a minute."}
            </DialogDescription>
          </DialogHeader>
          {parsed ? (
            <UploadQuoteReview
              parsed={parsed}
              coverages={coverages}
              onCoveragesChange={setCoverages}
              fees={fees}
              onFeesChange={setFees}
              disabled={confirm.isPending}
            />
          ) : (
            <Dropzone onChange={setFile} accept=".pdf" isLoading={parse.isPending} />
          )}
          <DialogFooter>
            {parsed ? (
              <>
                <Button variant="outline" onClick={() => setParsed(null)} disabled={confirm.isPending}>
                  Upload a different file
                </Button>
                <Button onClick={handleConfirm} loading={confirm.isPending ? 'Adding…' : undefined}>
                  Add quote
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setIsOpen(false)} disabled={parse.isPending}>
                  Cancel
                </Button>
                <Button
                  onClick={handleParse}
                  disabled={!file}
                  loading={parse.isPending ? 'Reading quote…' : undefined}
                >
                  Continue
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
